/**
 * BookCard: one book on the shelf. The whole card is a single link to the book; with `onClick` and no `href` it is a
 * button instead (PLAN.md §3, step 2: an app whose books open without changing its address), which `disabled` can turn off.
 * (cockatrice-ui `src/ui/BookCard.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/book-card.css';
import { createMemo, mergeProps, splitProps, untrack } from 'solid-js';
import Cover from './Cover.ts';
import type { CoverProps } from './Cover.ts';
import { component, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface BookCardProps extends Attributes {
  /** Where the book opens. */
  readonly href?: string | undefined;
  /** Book title, printed under the cover. */
  readonly title: string;
  /** Author, passed to the cover. */
  readonly author?: string | undefined;
  /** Stage chip: "Draft 2", "Editing", "Finished". */
  readonly stage?: string | undefined;
  /** Word count; numbers render as "48,210 words". */
  readonly words?: number | string | undefined;
  /** Cover settings: { bg, ink, kicker }, { src } or { kind: "empty" }. */
  readonly cover?: CoverProps | undefined;
  /** A short line after the count, e.g. when the book was last backed up. */
  readonly note?: Child;
  /** The button form only: can't be opened now (read-only books). */
  readonly disabled?: boolean | undefined;
  /** Click handler. */
  readonly onClick?: ((e: MouseEvent) => void) | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function BookCard(props: BookCardProps): Child {
  const [local, rest] = splitProps(props, ['href', 'title', 'author', 'stage', 'words', 'cover', 'class', 'onClick', 'note', 'disabled']);
  // `<Cover {...cover} />`: made again when the cover settings change, as their keys can change with them.
  const made = createMemo(() => {
    const settings = local.cover ?? {};
    return untrack(() =>
      component(
        Cover,
        mergeProps(settings, {
          get title() {
            return local.cover?.title ?? local.title;
          },
          get author() {
            return local.cover?.author ?? local.author;
          },
          decorative: true,
        }),
      ),
    );
  });
  const cover = (): Child => made();
  const inside = (): Child[] => [
    cover,
    el('span', { class: 'ui-book-card__title' }, () => local.title),
    el(
      'span',
      { class: 'ui-book-card__meta' },
      when(
        () => local.stage !== undefined && local.stage !== '',
        () => el('span', { class: 'ui-book-card__stage' }, () => local.stage),
      ),
      when(
        () => local.words !== undefined,
        () => el('span', {}, () => (typeof local.words === 'number' ? uiText()('ui.book-card.words', { words: local.words }) : local.words)),
      ),
      when(
        () => local.note !== undefined && local.note !== null && local.note !== '',
        () => el('span', { class: 'ui-book-card__note' }, () => local.note),
      ),
    ),
  ];
  const cls = (): string => `ui-book-card${local.class !== undefined && local.class !== '' ? ` ${local.class}` : ''}`;
  const onClick = (e: MouseEvent): void => {
    local.onClick?.(e);
  };
  if ((local.href === undefined || local.href === '') && local.onClick !== undefined) {
    return el('button', mergeProps({ type: 'button', class: cls, disabled: () => local.disabled === true, onClick }, rest), ...inside());
  }
  return el(
    'a',
    mergeProps({
      class: cls,
      href: () => (local.href !== undefined && local.href !== '' ? local.href : '#'),
      onClick,
    }, rest),
    ...inside(),
  );
}
